import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportClinicBillingItem } from '@interfaces/report';
import { FilterSelect } from '@interfaces/filter.interface';
import { Subscription } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';

export interface AddHiddenDataDialogData {
  hiddenData: ReportClinicBillingItem[];
}

@Component({
  selector: 'app-add-hidden-data-dialog',
  templateUrl: './add-hidden-data-dialog.component.html',
})
export class AddHiddenDataDialogComponent implements OnInit {

  public form: FormGroup;

  filteredData: ReportClinicBillingItem[] = [];
  selectedItems: Set<ReportClinicBillingItem> = new Set();

  displayedColumns: string[] = ['select', 'FIPACIENTE', 'NombrePaciente', 'FICIAP', 'FIMEDICA', 'NombreMedicamento'];
  dataSource = new MatTableDataSource<ReportClinicBillingItem>([]);

  filterOptions: FilterSelect[] = [
    {
      name: 'Filtros',
      filter: [
        { id: 'mutuasmuface', value: 'Mutuas/Muface', defaultState: 'include' },
        { id: 'athome', value: 'En casa', defaultState: 'include' },
        { id: 'isprivate', value: 'Privado', defaultState: 'include' },
        { id: 'outsidepharma', value: 'Farmacia externa', defaultState: 'include' },
        { id: 'paymed', value: 'De pago', defaultState: 'include' },
        { id: 'forcedpayment', value: 'Forzar pago', defaultState: 'include' },
        { id: 'recurrencepattern', value: 'Ignorar S/P', defaultState: 'include' }
      ]
    }
  ];

  activeFilters: Map<string, 'exclude' | 'include' | 'exclusive'> = new Map();

  private subs: Subscription[] = [];  

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AddHiddenDataDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddHiddenDataDialogData
  ) {
    this.form = this.formBuilder.group({
      filter: [''],
    });

    this.filteredData = [...data.hiddenData];
    this.dataSource.data = this.filteredData
  }

  ngOnInit(): void {
    // Inicializar filtros
    this.filterOptions[0].filter.forEach(filter => {
      this.activeFilters.set(filter.id, filter.defaultState || 'include');
    });
    this.applyFilters([{ filterId: '', mode: 'include' }] as any);

    this.subs.push(
      this.form.controls['filter'].valueChanges.subscribe((value) => {
        this.dataSource.filter = value;
      })
    )
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  applyFilters(filters: { filterId: string; mode: 'exclude' | 'include' | 'exclusive' }[]): void {
    // Actualizar el mapa de filtros activos
    filters.forEach(filter => {
      this.activeFilters.set(filter.filterId, filter.mode);
    });

    // Lógica de filtrado
    const exclusiveFilter = filters.find(f => f.mode === 'exclusive');

    if (exclusiveFilter) {
      // Modo exclusivo: solo mostrar elementos que coincidan con este filtro
      this.dataSource.data = this.data.hiddenData.filter(item => {
        return this.matchesFilter(item, exclusiveFilter.filterId);
      });
    } else {
      // Modo normal: mostrar elementos que coincidan con cualquier filtro 'include'
      const includeFilters = filters.filter(f => f.mode === 'include');

      if (includeFilters.length === 0) {
        this.dataSource.data = [];
      } else {
        this.dataSource.data = this.data.hiddenData.filter(item => {
          return includeFilters.some(filter => this.matchesFilter(item, filter.filterId));
        });
      }
    }

    // Limpiar selección de elementos que ya no están en dataSource.data
    this.selectedItems.forEach(item => {
      if (!this.dataSource.data.includes(item)) {
        this.selectedItems.delete(item);
      }
    });
  }

  matchesFilter(item: ReportClinicBillingItem, filterId: string): boolean {
    switch (filterId) {
      case 'athome':
        return !!item.AtHome;
      case 'forcedpayment':
        return !!item.ForcedPayment;
      case 'isprivate':
        return !!item.IsPrivate;
      case 'mutuasmuface':
        return !!item.MutuasMuface;
      case 'outsidepharma':
        return !!item.OutsidePharma;
      case 'recurrencepattern':
        return !item.RecurrencePattern;
      case 'paymed':
        return !item.PayMed;
      default:
        return false;
    }
  }

  toggleSelection(item: ReportClinicBillingItem): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  isSelected(item: ReportClinicBillingItem): boolean {
    return this.selectedItems.has(item);
  }

  toggleAll(event: any): void {
    if (event.checked) {
      this.filteredData.forEach(item => this.selectedItems.add(item));
    } else {
      this.selectedItems.clear();
    }
  }

  isAllSelected(): boolean {
    return this.filteredData.length > 0 && this.filteredData.every(item => this.selectedItems.has(item));
  }

  isIndeterminate(): boolean {
    const selectedCount = this.filteredData.filter(item => this.selectedItems.has(item)).length;
    return selectedCount > 0 && selectedCount < this.filteredData.length;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    this.dialogRef.close(Array.from(this.selectedItems));
  }
}
